<template>
    <div v-if="isLoading">
        <span class="fa fa-spinner fa-spin"></span>
    </div>
    <div v-else>
        <b-row>
            <b-col>
                <div class="accordion">
                    <!-- SCAN LABEL -->
                    <b-card class="mb-1" no-body :class="{inactive: !isStepActive('label-scan')}" ref="label-scan">
                        <b-card-header class="text-left py-2 pl-3">
                            <b-card-title class="m-0">SCAN LABEL</b-card-title>
                        </b-card-header>
                        <b-collapse id="accordion-label-scan" visible accordion="orderpicking-steps">
                            <b-card-body class="p-3">
                                <label-scan v-if="dispatchRequest === null"
                                            :location="location"
                                            @success="finishScanLabel"
                                />
                                <div v-else-if="dispatchRequest === false">
                                    <b-alert show fade variant="warning">
                                        Er is geen retourzending gevonden voor deze barcode.
                                        Leg de producten in deze order apart, of probeer het opnieuw.
                                    </b-alert>

                                    <b-button variant="primary" @click="dispatchRequest = null">Volgende</b-button>
                                </div>
                            </b-card-body>
                        </b-collapse>
                    </b-card>

                    <!-- SCAN PRODUCT -->
                    <b-card class="mb-1" no-body :class="{inactive: !isStepActive('product-scan')}" ref="product-scan">
                        <b-card-header class="text-left py-2 pl-3">
                            <b-card-title class="m-0">SCAN ARTIKEL</b-card-title>
                        </b-card-header>
                        <b-collapse id="accordion-product-scan" visible accordion="orderpicking-steps">
                            <b-card-body class="p-3" :data-sample-ean="sampleEan">
                                <product-scan-return v-if="productEan === null" @success="finishScanProduct" :key="currentStep"/>
                                <div v-else-if="productEan === false">
                                    <b-alert show fade variant="warning">
                                        Dit product is niet gevonden in deze retourzending.
                                        Leg het product apart,
                                        of <b-button variant="link" @click="productEan = null" size="sm" class="p-0">probeer het opnieuw</b-button>.
                                    </b-alert>

                                    <b-button variant="primary" @click="setActiveStep('choose-next-step')">Volgende</b-button>
                                </div>
                            </b-card-body>
                        </b-collapse>
                    </b-card>

                    <!-- CHECK SELLABLE -->
                    <b-card class="mb-1" no-body :class="{inactive: !isStepActive('check-sellable')}" ref="check-sellable">
                        <b-card-header class="text-left py-2 pl-3">
                            <b-card-title class="m-0">STAAT ARTIKEL</b-card-title>
                        </b-card-header>
                        <b-collapse id="accordion-check-sellable" visible accordion="orderpicking-steps">
                            <b-card-body class="p-3">
                                <check-sellable v-if="productEan" :key="currentStep"
                                                :process-type="processType" :location-identifier="location.identifier"
                                                :location-type-identifier="location.location_type.identifier" :storage-zone-identifier="storageZoneIdentifier"
                                                :product-ean="productEan"
                                                @success="finishProductStateStep"
                                />
                            </b-card-body>
                        </b-collapse>
                    </b-card>

                    <!-- SCAN LEGPLAATS -->
                    <b-card class="mb-1" no-body :class="{inactive: !isStepActive('sorting-location-scan')}" ref="sorting-location-scan">
                        <b-card-header class="text-left py-2 pl-3">
                            <b-card-title class="m-0">SCAN LEGPLAATS</b-card-title>
                        </b-card-header>
                        <b-collapse id="accordion-sorting-location-scan" visible accordion="orderpicking-steps">
                            <b-card-body class="p-3">
                                <sorting-location-scan v-if="sortingLocation && dispatchRequest" :key="currentStep"
                                                       :storage-location-display-name="sortingLocation.display_name" :storage-location-identifier="sortingLocation.identifier"
                                                       :dispatch-request="dispatchRequest" :product-identifier="productEan" :process-type="processType"
                                                       :location="location" :storage-zone-identifier="storageZoneIdentifier"
                                                       :product-state="productState"
                                                       @success="finishSortingLocationScan"
                                />
                            </b-card-body>
                        </b-collapse>
                    </b-card>

                    <!-- CHOOSE NEXT STEP -->
                    <b-card class="mb-1" no-body :class="{inactive: !isStepActive('choose-next-step')}" ref="choose-next-step">
                        <b-card-header class="text-left py-2 pl-3">
                            <b-card-title class="m-0">VOLGENDE</b-card-title>
                        </b-card-header>
                        <b-collapse id="accordion-choose-next-step" visible accordion="orderpicking-steps">
                            <b-card-body class="p-3">
                                <b-row>
                                    <b-col cols="12" md="4">
                                        <b-button variant="primary" @click="resetSteps()" class="d-block w-100 mb-4">Volgende retourzending</b-button>
                                        <b-button v-if="dispatchRequest" variant="primary" @click="resetToProductScanStep()" class="d-block w-100">Scan een ander product uit dezelfde retourzending</b-button>
                                    </b-col>
                                </b-row>
                            </b-card-body>
                        </b-collapse>
                    </b-card>

                </div>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import _ from "lodash"
import PageTitle from "../../components/PageTitle.vue";
import SortingLocationScan from "./Steps/SortingLocationScan.vue";
import LocationLink from "../../components/LocationLink.vue";
import LabelScan from "./Steps/LabelScan.vue";
import {next} from "lodash/seq";
import ProductScanReturn from "./Steps/ProductScanReturn.vue";
import CheckSellable from "./Steps/CheckSellable.vue";

export const ORDER_COMPLETION_PROCESSES = {
    MARK_SORTING_LOCATION_AS_READY: 'mark_sorting_location_as_ready',
}

export default {
    name: "ProcessReturn",
    components: {
        CheckSellable,
        ProductScanReturn,
        LabelScan, LocationLink,
        SortingLocationScan,
        PageTitle
    },

    props: {
        location: {required: true, type: Object},
        storageZoneIdentifier: {required: true, type: String},
        processType: {required: true, type: String},
    },

    data() {
        return {
            isLoading: false,
            currentStep: '',
            productEan: null,
            dispatchRequest: null,
            productState: null,
            isLastScan: null,
            sortingLocation: null,
            finishedSortingLocationStep: false,
        }
    },

    computed: {
        sampleEan() {
            try {
                return this.dispatchRequest.dispatch_request_lines[0].sales_order_line.ean_codes
            }
            catch (e) {
                return ''
            }
        }
    },

    mounted() {
        this.$root.$on('bv::collapse::state', (collapseId, isJustShown) => {
            if (collapseId.toString().startsWith('accordion-') && isJustShown) {
                this.currentStep = collapseId
            }
        })
    },

    beforeDestroy() {
        this.$root.$off("bv::collapse::state");
    },

    methods: {
        next,
        getPageTitle() {
            return 'Retouren scannen'
        },
        nextStep() {
            if (!this.dispatchRequest && !this.finishedSortingLocationStep) {
                this.resetSteps()
            }
            else if (this.productEan === null) {
                this.setActiveStep('product-scan')
            }
            else if (!this.sortingLocation) {
                this.setActiveStep('check-sellable')
            }
            else if (!this.finishedSortingLocationStep) {
                this.setActiveStep('sorting-location-scan')
            }
            else if (this.finishedSortingLocationStep) {
                this.setActiveStep('choose-next-step')
            }
            else {
                this.resetSteps()
            }
        },
        resetSteps() {
            this.dispatchRequest = null
            this.productEan = null
            this.productState = null
            this.sortingLocation = null
            this.isLastScan = null
            this.finishedSortingLocationStep = false
            this.setActiveStep('label-scan')
        },
        resetToProductScanStep() {
            this.productEan = null
            this.productState = null
            this.isLastScan = null
            this.sortingLocation = null
            this.finishedSortingLocationStep = false
            this.setActiveStep('product-scan')
        },
        setActiveStep(step) {
            if (this.isStepActive(step)) {
                return
            }

            this.$root.$emit('bv::toggle::collapse', `accordion-${step}`)
            this.$smoothScroll({
                scrollTo: this.$refs[step],
                updateHistory: false,
                offset: -140, // FIXME: calculate dynamically
            })
        },
        isStepActive(step) {
            return this.currentStep === `accordion-${step}`
        },
        finishScanLabel(dispatchRequest) {
            this.dispatchRequest = dispatchRequest
            if (dispatchRequest === false) {
                return
            }

            this.nextStep()
        },
        finishScanProduct(productEan) {
            let isProductFoundInDRQ = false
            this.dispatchRequest.dispatch_request_lines.forEach(line => {
                const eans = line.sales_order_line.ean_codes.split(' ')
                if (eans.indexOf(productEan) !== -1) {
                    isProductFoundInDRQ = true
                }
            })

            this.productEan = (isProductFoundInDRQ) ? productEan : false
            if (!this.productEan) {
                return
            }

            this.nextStep()
        },
        finishProductStateStep(productState, storageLocation) {
            this.productState = productState
            this.sortingLocation = storageLocation
            this.finishedSortingLocationStep = false
            this.nextStep()
        },
        finishSortingLocationScan(responseData) {
            this.dispatchRequest = responseData.dispatch_request
            this.finishedSortingLocationStep = true
            this.nextStep()
        },
    },
}
</script>

<style scoped lang="scss">
.accordion {
    .inactive {
        opacity: 0.4;
        //display: none;
    }
}
</style>
